import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/fuse-template/core/auth/auth.service';
import { formatKennitala, formatKennitalaRemoveLine } from 'app/helpers/formatKennitala';
import { formatPhoneNumber } from 'app/helpers/formatPhoneNumber';
import { ValidateKennitalaWithHyphen, ValidatePhoneSpace } from 'app/helpers/validators';
import { ExposedInitiateLoginRes } from 'app/interfaces/responses/Exposed/exposedInitiateLoginRes';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { LoginStatus } from 'common/constants/login-status.constants';
import { Subscription, catchError, finalize, fromEvent, map, of, timer } from 'rxjs';

@Component({
    selector: 'auth-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class AuthSignInComponent implements OnInit, OnDestroy {
    @ViewChild('signInNgForm') signInNgForm: NgForm;
    @Input() isDialog: boolean = false;

    @Input() selectedPanelIndex: number = 0;

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    initiateResponse: ExposedInitiateLoginRes | null = null;
    signInForm: UntypedFormGroup;
    showAlert: boolean = false;
    nameLastLoggedIn: string = '';
    pollingSubscription: Subscription | null = null;
    visibilitySubscription: Subscription;
    pollingInProgress = false;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _authService: AuthService,
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        public dialog: MatDialog,
        private translationService: TranslationService
    ) {
        // Stop polling when the application tab is not visible
        this.visibilitySubscription = fromEvent(document, 'visibilitychange').subscribe(() => {
            if (document.hidden && this.pollingSubscription) {
                this.pollingSubscription.unsubscribe();
                this.pollingSubscription = null;
            } else if (!document.hidden && this.pollingSubscription === null) {
                if (this.initiateResponse) {
                    this.pollingSubscription = this.pollLoginStatus(this.initiateResponse.authRequestId, this.initiateResponse.interval);
                }
            }
        });
    }

    //#region Remember me

    rememberMeCheck() {
        let rememberMe: boolean = true;
        try {
            if (localStorage.getItem('rememberMe') == '0') {
                this.rememberMeClear();
                rememberMe = false;
            }
        } catch {}
        return rememberMe;
    }

    rememberMePhone() {
        try {
            if (localStorage.getItem('rememberMe') != '0') {
                return localStorage.getItem('phone') ?? '';
            } else {
                return '';
            }
        } catch {
            return '';
        }
    }

    rememberMeSSN() {
        try {
            if (localStorage.getItem('rememberMe') != '0') {
                return localStorage.getItem('ssn') ?? '';
            } else {
                return '';
            }
        } catch {
            return '';
        }
    }

    rememberMeTabInit() {
        try {
            if (localStorage.getItem('rememberMe') != '0') {
                this.selectedPanelIndex = +localStorage.getItem('selectedPanelIndex');
            } else {
                this.selectedPanelIndex = 0;
            }
        } catch {
            this.selectedPanelIndex = 0;
        }
    }

    rememberMeChange(e) {
        if (e.checked) {
            localStorage.setItem('rememberMe', '1');
        } else {
            this.rememberMeClear();
        }
    }

    rememberMeClear() {
        localStorage.setItem('rememberMe', '0');
        localStorage.setItem('phone', '');
        localStorage.setItem('ssn', '');
        localStorage.setItem('rememberMeName', '');
        localStorage.setItem('selectedPanelIndex', '0');
        this.nameLastLoggedIn = '';
    }

    tabChanged(event: MatTabChangeEvent) {
        if (event.index == 0) {
            this.selectedPanelIndex = 0;
        } else if (event.index == 1) {
            this.selectedPanelIndex = 1;
        }
        localStorage.setItem('selectedPanelIndex', this.selectedPanelIndex.toString());
    }

    determineValidators() {
        this.signInForm.get('ssn').clearValidators();
        this.signInForm.get('phone').clearValidators();
        if (this.selectedPanelIndex == 0) {
            this.signInForm.get('phone').setValidators([Validators.required, ValidatePhoneSpace]);
        } else {
            this.signInForm.get('ssn').setValidators([Validators.required, ValidateKennitalaWithHyphen]);
        }
        this.signInForm.get('phone').updateValueAndValidity();
        this.signInForm.get('ssn').updateValueAndValidity();
    }

    //#endregion

    phoneKeyUp(e) {
        var _phone = this.signInNgForm.control.get('phone').value;
        this.signInNgForm.control.get('phone').setValue(formatPhoneNumber(_phone));
        this.rememberMeClear();
        this.showAlert = false;
    }

    ssnKeyUp(e) {
        var _ssn = this.signInNgForm.control.get('ssn').value;
        this.signInNgForm.control.get('ssn').setValue(formatKennitala(_ssn));
        this.rememberMeClear();
        this.showAlert = false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.rememberMeTabInit();
        // Create the form
        this.signInForm = this._formBuilder.group({
            phone: [this.rememberMePhone()],
            ssn: [this.rememberMeSSN()],
            rememberMe: [this.rememberMeCheck()],
        });
        try {
            this.nameLastLoggedIn = localStorage.getItem('rememberMeName');
        } catch {}
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.pollingSubscription?.unsubscribe();
        this.pollingSubscription = null;
        this.visibilitySubscription.unsubscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    clearPollingSubscription() {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }

        this.initiateResponse = null;
    }

    /**
     * Sign in
     */
    signIn(): void {
        this.determineValidators();
        // Return if the form is invalid
        if (this.signInForm.invalid) {
            return;
        }

        if (this.signInForm.value.rememberMe) {
            localStorage.setItem('phone', this.signInForm.value.phone);
            localStorage.setItem('ssn', this.signInForm.value.ssn);
            localStorage.setItem('rememberMe', '1');
        } else {
            this.rememberMeClear();
        }

        // Disable the form
        this.signInForm.disable();

        this.initiateResponse = null;

        this.signInForm.value.phone = this.signInForm.value.phone.replace(/ /g, '');

        // Hide the alert
        this.showAlert = false;

        this._authService
            .initiateLogin({
                phoneNumber: this.selectedPanelIndex == 0 ? this.signInForm.value.phone : null,
                socialSecurityNumber: this.selectedPanelIndex == 1 ? formatKennitalaRemoveLine(this.signInForm.value.ssn) : null,
                authenticationType: this.selectedPanelIndex == 0 ? 'audkenniSim' : 'audkenniApp',
            })
            .subscribe({
                next: (response) => {
                    this.initiateResponse = response;
                    this.signInForm.value.verificationCode = response.verificationCode;

                    // Start polling
                    if (!this.pollingSubscription) {
                        this.pollingSubscription = this.pollLoginStatus(response.authRequestId, response.interval, response.interval);
                    }
                },
                error: (response) => {
                    console.log('ERROR');
                    // Re-enable the form
                    this.signInForm.enable();

                    // Set the alert
                    this.alert = {
                        type: 'error',
                        message: response.error.errorItem.friendlyMessage,
                    };

                    // Show the alert
                    this.showAlert = true;
                },
            });
    }

    // Check login status periodically until the user completes the authentication or an error occurs
    pollLoginStatus(authRequestId: string, interval: number = 5, startDelay: number = 0) {
        return timer(startDelay * 1000, interval * 1500).subscribe(() => {
            if (this.pollingInProgress) {
                return;
            }
            this.pollingInProgress = true;
            this._authService
                .checkLoginStatus({
                    authRequestId: authRequestId,
                })
                .pipe(
                    map((res) => {
                        if (res && res.loginStatus === LoginStatus.Success) {
                            this.clearPollingSubscription();

                            try {
                                this.dialog.closeAll();
                            } catch {}

                            if (!this.isDialog) {
                                const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || '/signed-in-redirect';

                                // Navigate to the redirect url
                                this._router.navigateByUrl(redirectURL);
                            }
                        }
                    }),
                    catchError((err) => {
                        this.clearPollingSubscription();

                        // Re-enable the form
                        this.signInForm.enable();

                        if (
                            err.error.errorItem.message == this.translationService.translate('error_user_cancelled_action') ||
                            err.error.errorItem.message == this.translationService.translate('error_time_limit_exceeded')
                        ) {
                            // Set the alert
                            this.alert = {
                                type: 'error',
                                message: this.translationService.translate('error_user_not_authorized'),
                            };
                        } else {
                            // Set the alert
                            this.alert = {
                                type: 'error',
                                message: err.error.errorItem.friendlyMessage,
                            };
                        }
                        // Show the alert
                        this.showAlert = true;

                        return of(null);
                    }),
                    finalize(() => {
                        this.pollingInProgress = false;
                    })
                )
                .subscribe();
        });
    }
}
